import React, { useState, useRef, useEffect, useCallback } from "react";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Check, StickyNote } from "lucide-react";
import { logActivity } from "@/lib/activityLogger";

interface InquiryNotesPanelProps {
  inquiryId: string;
  initialNotes: string | null;
}

export function InquiryNotesPanel({ inquiryId, initialNotes }: InquiryNotesPanelProps) {
  const [notes, setNotes] = useState(initialNotes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousNotesRef = useRef(initialNotes || '');

  // Autosave notes with debounce - also auto-transitions status to 'contacted'
  const saveNotes = useCallback(async (value: string, currentStatus?: string) => {
    setSavingNotes(true);
    setNotesSaved(false);
    try {
      const { error } = await supabase
        .from('contact_submissions')
        .update({ notes: value.trim() || null })
        .eq('id', inquiryId);
      
      if (error) throw error;
      setNotesSaved(true);
      
      // Log activity only if notes actually changed
      if (value.trim() !== previousNotesRef.current.trim()) {
        logActivity({ inquiryId, action: 'note_added', description: 'Notes updated', metadata: { preview: value.slice(0, 100) } });
        previousNotesRef.current = value.trim();
        
        // Auto-transition status to 'contacted' if currently pending or call_scheduled
        // We need to fetch current status since we don't have it as prop
        const { data: inquiry } = await supabase
          .from('contact_submissions')
          .select('status')
          .eq('id', inquiryId)
          .single();
        
        if (inquiry && ['pending', 'call_scheduled'].includes(inquiry.status)) {
          await supabase
            .from('contact_submissions')
            .update({ status: 'contacted' })
            .eq('id', inquiryId);
          
          logActivity({
            inquiryId,
            action: 'status_changed',
            description: 'Status changed to Contacted (notes activity)',
            metadata: { oldStatus: inquiry.status, newStatus: 'contacted', trigger: 'notes_edit' }
          });
        }
      }
      
      setTimeout(() => setNotesSaved(false), 2000);
    } catch (err: any) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  }, [inquiryId]);

  const handleNotesChange = (value: string) => {
    setNotes(value);
    setNotesSaved(false);
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce save (1 second after typing stops)
    saveTimeoutRef.current = setTimeout(() => {
      saveNotes(value);
    }, 1000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="border rounded-lg p-3 bg-card">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-sm flex items-center gap-2">
          <StickyNote className="h-4 w-4" /> Notes
        </h4>
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          {savingNotes && <Loader2 className="h-3 w-3 animate-spin" />}
          {notesSaved && <><Check className="h-3 w-3 text-primary" /> Saved</>}
        </div>
      </div>
      <Textarea
        value={notes}
        onChange={(e) => handleNotesChange(e.target.value)}
        placeholder="Click to add notes..."
        className="text-xs min-h-[80px] resize-none border-transparent bg-transparent hover:bg-muted/30 focus:bg-background focus:border-input transition-colors"
      />
    </div>
  );
}
